import {
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
  Select,
  MenuItem,
  FormControlLabel,
  FormHelperText,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import emailStyle from "./Email.module.css";
import { Send } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { APIEmail, deleteEmail, emptyEmail, saveEmail } from "../api/emails";
import {
  APIEmailTemplate,
  getAllEmailTemplates,
  getEmailTemplate,
} from "../api/emailTemplates";
import { APIProposal, getAllProposals, getProposal } from "../api/proposals";
import { APICustomer, getAllCustomers, getCustomer } from "../api/customers";
import Handlebars from "handlebars";

function EmailDetailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const refId = searchParams.get("refId") || "";
  const refType = searchParams.get("refType") || "";
  const templateId = searchParams.get("templateId") || "";
  const navigate = useNavigate();

  const [email, setEmailData] = useState<APIEmail>(emptyEmail());
  const [customers, setCustomers] = useState<APICustomer[]>([]);
  const [proposals, setProposals] = useState<APIProposal[]>([]);
  const [templates, setTemplates] = useState<APIEmailTemplate[]>([]);

  useEffect(() => {
    getAllCustomers().then((v) => setCustomers(v));
    getAllProposals().then((v) => setProposals(v));
  }, []);

  useEffect(() => {
    getAllEmailTemplates().then((v) => {
      const f = v.filter((et) => et.refType == refType);
      setTemplates(f);
    });
  }, [refType]);

  useEffect(() => {
    (async () => {
      const template = await getEmailTemplate(templateId || undefined);
      // Without a template, what the email is about comes from the page.
      const type = template.refType || refType;
      let refVal: any = {};
      if (refId && type == "proposal") {
        refVal = await getProposal(refId);
      }
      if (refId && type == "customer") {
        refVal = await getCustomer(refId);
      }

      const newEmail = emptyEmail();

      newEmail.to = refVal.email || "";
      newEmail.from = template.from;
      newEmail.attachments = template.attachments;
      newEmail.refType = type;
      newEmail.refId = refId || undefined;

      newEmail.subject = Handlebars.compile(template.subject)(refVal);
      newEmail.body = Handlebars.compile(template.body)(refVal);
      newEmail.templateId = templateId || undefined;
      setEmailData(newEmail);
    })();
  }, [templateId, refId, refType]);

  const [isModified, setIsModified] = useState(false);

  // Handle changes
  const changeEmail = (change: Partial<APIEmail>) => {
    setSearchParams((prev) => {
      if (change.refType) {
        prev.set("refType", change.refType);
      }
      if (change.refId) {
        prev.set("refId", change.refId);
      }
      if (change.templateId) {
        prev.set("templateId", change.templateId);
      }
      return prev;
    });

    setEmailData({ ...email, ...change });
  };

  const canSave = () => {
    // TODO: Add validation
    return true;
  };

  const handleSave = async () => {
    const t = await saveEmail({ ...email, generatedAt: new Date().getTime() });
    navigate(`/emails?id=${t.id}`, { replace: true });
  };

  const canSend = email.to.trim() !== "" && email.subject.trim() !== "";

  return (
    <div className={emailStyle.compose}>
      <Typography variant="h6">New email</Typography>
      <section className={emailStyle.composeSection}>
        <Typography variant="subtitle2" color="text.secondary">
          About
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={refType}
          onChange={(_, v) => v && changeEmail({ refType: v })}
        >
          <ToggleButton value="customer">Customer</ToggleButton>
          <ToggleButton value="proposal">Proposal</ToggleButton>
        </ToggleButtonGroup>
        <div className={emailStyle.composeRow}>
          <FormControl disabled={!refType}>
            <InputLabel>{refType === "proposal" ? "Proposal" : "Customer"}</InputLabel>
            <Select
              value={refId}
              onChange={(e) => changeEmail({ refId: e.target.value })}
              label={refType === "proposal" ? "Proposal" : "Customer"}
            >
              {refType == "customer" &&
                customers.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.firstName} {r.lastName}
                  </MenuItem>
                ))}
              {refType == "proposal" &&
                proposals.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.firstName} {r.lastName} ·{" "}
                    {new Date(r.createdAt).toLocaleDateString()}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl disabled={templates.length === 0}>
            <InputLabel>Template</InputLabel>
            <Select
              value={email.templateId}
              onChange={(e) => changeEmail({ templateId: e.target.value })}
              label="Template"
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.title}
                </MenuItem>
              ))}
            </Select>
            {refType && templates.length === 0 && (
              <FormHelperText>
                No {refType} templates yet. Add one under Email Templates.
              </FormHelperText>
            )}
          </FormControl>
        </div>
      </section>

      <section className={emailStyle.composeSection}>
        <Typography variant="subtitle2" color="text.secondary">
          Message
        </Typography>
        <div className={emailStyle.composeRow}>
          <FormControl>
            <InputLabel htmlFor="toInput">To</InputLabel>
            <OutlinedInput
              value={email.to}
              onChange={(e) => changeEmail({ to: e.target.value })}
              id="toInput"
              label="To"
              type="email"
            />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="fromInput">From</InputLabel>
            <OutlinedInput
              value={email.from}
              onChange={(e) => changeEmail({ from: e.target.value })}
              id="fromInput"
              label="From"
              placeholder="The connected Google account"
            />
          </FormControl>
        </div>
        <FormControl>
          <InputLabel htmlFor="subjectInput">Subject</InputLabel>
          <OutlinedInput
            value={email.subject}
            onChange={(e) => changeEmail({ subject: e.target.value })}
            id="subjectInput"
            label="Subject"
          />
        </FormControl>
        <FormControl>
          <InputLabel htmlFor="bodyInput">Message</InputLabel>
          <OutlinedInput
            value={email.body}
            onChange={(e) => changeEmail({ body: e.target.value })}
            multiline
            minRows={8}
            id="bodyInput"
            label="Message"
          />
        </FormControl>
        {refType === "proposal" && (
          <FormControlLabel
            control={
              <Switch
                checked={email.attachments}
                onChange={(e) => changeEmail({ attachments: e.target.checked })}
              />
            }
            label="Attach the proposal PDF"
          />
        )}
      </section>

      <div>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={handleSave}
          disabled={!canSend}
        >
          Send
        </Button>
      </div>
    </div>
  );
}

export default EmailDetailsPage;
